import React, { useMemo } from 'react';
import { List } from 'immutable';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    CallRecordKey,
    ExtensionKey,
    QueueKey,
    TicketKey,
} from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { AppDispatch } from 'flow/types/Redux';
import {
    createTicketForQueueCall,
    updateTicketAssignee,
} from 'flow/actions/tickets-action';
import {
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { selectCallRecordOmniTicketEntityKey } from 'flow/selectors/call-records-selectors';
import {
    selectAssignee,
    selectMembers,
    selectOwnUserHasCapabilityTickets,
    selectTicketIsFetched,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import {
    makeSelectOwnUserIsMemberOfQueue,
    selectQueueMembers,
} from 'flow/selectors/queue-selectors';
import { getTicketAssigneeOptionsFromMembers } from 'flow/utils/ticket-utils';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import Skeleton from 'flow/components/ui-components/Skeleton';
import SelectAssigneeAvatar from 'flow/components/tickets/Assignee/SelectAssigneeAvatar';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useTicketAction from 'flow/hooks/useTicketAction';

interface Props {
    callRecordKey: CallRecordKey;
    queueKey: QueueKey;
}

const MenuItemAssignTo = ({ callRecordKey, queueKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const ticketKey = useAppSelector((state) =>
        selectCallRecordOmniTicketEntityKey(state, callRecordKey),
    );

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensions = useAppSelector(selectExtensions);

    const hasCapabilityTickets = useAppSelector(
        selectOwnUserHasCapabilityTickets,
    );

    const ticketAllowsAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );

    const selectIsMemberOfQueue = useMemo(makeSelectOwnUserIsMemberOfQueue, []);
    const isMemberOfQueue = useAppSelector((state) =>
        selectIsMemberOfQueue(state, queueKey),
    );

    // With no ticket yet, there is nothing to resolve against: queue membership is
    // what decides whether this user may create one and assign it.
    const canAssignTicket = ticketKey
        ? ticketAllowsAssign
        : hasCapabilityTickets && !!isMemberOfQueue;

    const ticketMembers = useAppSelector((state) =>
        selectMembers(state, ticketKey),
    );

    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );

    const queueMemberExtensionKeys: List<ExtensionKey> = useMemo(
        () =>
            queueMembers
                ? List(queueMembers.map((member) => member.get('extensionKey')))
                : List<ExtensionKey>(),
        [queueMembers],
    );

    const memberExtensionKeys = ticketKey
        ? ticketMembers
        : queueMemberExtensionKeys;

    const assigneeOptions = useMemo(
        () =>
            getTicketAssigneeOptionsFromMembers(
                i18next,
                ownExtensionKey,
                memberExtensionKeys,
                extensions,
            ),
        [i18next, ownExtensionKey, memberExtensionKeys, extensions],
    );

    const ticketIsFetched = useAppSelector((state) =>
        selectTicketIsFetched(state, ticketKey),
    );

    const currentAssignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );

    if (!canAssignTicket) {
        const getDisabledTooltip = () => {
            if (!hasCapabilityTickets) {
                return i18next.t('tickets.action.license_required');
            }
            if (ticketKey) {
                return i18next.t('tickets.action.ticket_membership_required');
            }
            return i18next.t('tickets.action.queue_membership_required');
        };
        return (
            <Tooltip label={getDisabledTooltip()}>
                <MenuItem disabled={true}>
                    {i18next.t('ticket.assign.to')}
                </MenuItem>
            </Tooltip>
        );
    }

    const displayLoading = ticketKey && !ticketIsFetched;

    const onAssignError = (error: { status?: number }) => {
        if (error.status === 404) {
            openModal('confirmationDialog', {
                title: i18next.t('tickets.assign.license_required.title'),
                description: i18next.t(
                    'tickets.assign.license_required.description',
                ),
                handleAccept: () => {},
                hideCancelButton: true,
            });
        }
    };

    const handleSelectAssignee = (
        event: React.MouseEvent,
        assignee: ExtensionKey | 'unassigned',
    ) => {
        event.stopPropagation();
        if (ticketKey) {
            runTicketAction(updateTicketAssignee(ticketKey, assignee)).catch(
                onAssignError,
            );
        } else {
            void dispatch(
                createTicketAndAssign(
                    queueKey,
                    callRecordKey,
                    assignee,
                    runTicketAction,
                    onAssignError,
                ),
            );
        }
    };

    return (
        <NestedMenuItem title={i18next.t('ticket.assign.to')}>
            {displayLoading
                ? Array.from({ length: 3 }, (_, i) => (
                      <div key={i} className="flex w-56 items-center p-2">
                          <Skeleton>
                              <Skeleton.Avatar className="me-3 size-6 rounded-full" />
                              <Skeleton.Text className="w-24" />
                          </Skeleton>
                      </div>
                  ))
                : assigneeOptions.map((option) => (
                      <MenuItem
                          key={option.key}
                          onClick={(event) =>
                              handleSelectAssignee(event, option.key)
                          }
                          className="w-56"
                      >
                          <div className="me-3 flex">
                              <SelectAssigneeAvatar assigneeKey={option.key} />
                          </div>
                          {option.label}
                          {currentAssignee === option.key ? (
                              <div className="ms-auto">
                                  <Icon iconKey="done" size="1rem" />
                              </div>
                          ) : null}
                      </MenuItem>
                  ))}
        </NestedMenuItem>
    );
};

export default MenuItemAssignTo;

const createTicketAndAssign =
    (
        queueKey: QueueKey,
        callRecordKey: CallRecordKey,
        assignee: ExtensionKey | 'unassigned',
        // The assignment lands on a ticket that does not exist yet, so it has to
        // run in here. Handed in rather than reached for, since a hook cannot be
        // called from a thunk.
        runTicketAction: ReturnType<typeof useTicketAction>,
        onAssignError: (error: { status?: number }) => void,
    ) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const ownExtensionKey = selectOwnExtensionKey(getState());
        const queueMemberKeys = List(
            selectQueueMembers(getState(), queueKey).map((member) =>
                member.get('extensionKey'),
            ),
        );
        const queueMembersIncludingOwnUser = queueMemberKeys?.includes(
            ownExtensionKey,
        )
            ? queueMemberKeys
            : queueMemberKeys?.insert(0, ownExtensionKey);
        const membersForApi =
            assignee !== 'unassigned' &&
            !queueMembersIncludingOwnUser?.includes(assignee)
                ? queueMembersIncludingOwnUser?.push(assignee)
                : queueMembersIncludingOwnUser;

        return dispatch(
            createTicketForQueueCall(callRecordKey, membersForApi, null),
        ).then((data: { key: TicketKey }) =>
            runTicketAction(updateTicketAssignee(data.key, assignee)).catch(
                onAssignError,
            ),
        );
    };
